/**
 * City theme definitions — full style guide spec.
 *
 * Each city gets a distinct visual identity injected as CSS custom properties
 * via useCityThemeInjection. Platform chrome (nav, auth, checkout, errors)
 * uses base Shadcn tokens and is never overridden here.
 */

export type HeroTreatment = 'warm-overlay' | 'high-contrast' | 'cool-overlay' | 'dark-glass';

// ─── Color palette ────────────────────────────────────────────────────────────

export interface CityColorSet {
  primary: string;
  primaryForeground: string;
  accent: string;
  accentForeground: string;
  background: string;
  card: string;
  foreground: string;
  mutedForeground: string;
  border: string;
}

// ─── Typography scale ─────────────────────────────────────────────────────────

export interface CityTypescale {
  headingFont: string; // Google Fonts name
  bodyFont: string;
  headingStack: string; // CSS font-family value
  bodyStack: string;
  /** rem values */
  sizeHero: string;
  sizeH1: string;
  sizeH2: string;
  sizeH3: string;
  sizeBody: string;
  sizeSmall: string;
  headingWeight: string;
  headingTracking: string; // letter-spacing
  headingLeading: string; // line-height
  bodyLeading: string;
}

// ─── Spacing + shape ──────────────────────────────────────────────────────────

export interface CityShape {
  radiusSm: string;
  radiusMd: string;
  radiusLg: string;
  radiusPill: string;
  shadowCard: string;
  shadowHover: string;
}

// ─── Motion ───────────────────────────────────────────────────────────────────

export interface CityMotion {
  transitionBase: string;
  transitionHero: string;
}

// ─── Component tokens ─────────────────────────────────────────────────────────

export interface CityComponentTokens {
  /** Button */
  btnPrimaryBg: string;
  btnPrimaryFg: string;
  btnPrimaryBorder: string;
  btnRadius: string;
  btnTracking: string;
  btnTransform: string; // CSS text-transform
  btnWeight: string;
  /** Card */
  cardBg: string;
  cardBorder: string;
  cardRadius: string;
  cardImageRadius: string;
  /** Badge */
  badgeBg: string;
  badgeFg: string;
  badgeRadius: string;
  /** Input */
  inputBg: string;
  inputBorder: string;
  inputFocusRing: string;
  inputRadius: string;
  /** Hero */
  heroMinHeight: string;
  heroHeadingColor: string;
  heroSubheadingColor: string;
}

// ─── Full theme ───────────────────────────────────────────────────────────────

export interface CityTheme {
  geographySlug: string;
  displayName: string;
  /** Whether this city defaults to dark mode */
  defaultDark: boolean;
  heroTreatment: HeroTreatment;
  colors: CityColorSet;
  colorsDark: CityColorSet;
  typescale: CityTypescale;
  shape: CityShape;
  motion: CityMotion;
  components: CityComponentTokens;
  /** Component token overrides applied when dark mode is active */
  componentsDark?: Partial<CityComponentTokens>;
  /** Component token overrides applied when light mode is active (useful when defaultDark: true) */
  componentsLight?: Partial<CityComponentTokens>;
}

// ─── City definitions ─────────────────────────────────────────────────────────

export const CITY_THEMES: Record<string, CityTheme> = {
  /**
   * Kalovate — original platform brand style, preserved for reference/restore.
   */
  kalovate: {
    geographySlug: 'kalovate',
    displayName: 'Kalovate',
    defaultDark: false,
    heroTreatment: 'cool-overlay',
    colors: {
      primary: 'hsl(214, 100%, 59%)',
      primaryForeground: 'hsl(0, 0%, 98%)',
      accent: 'hsl(24, 100%, 55%)',
      accentForeground: 'hsl(0, 0%, 98%)',
      background: 'hsl(0, 0%, 100%)',
      card: 'hsl(0, 0%, 100%)',
      foreground: 'hsl(222, 84%, 4.9%)',
      mutedForeground: 'hsl(215, 16%, 47%)',
      border: 'hsl(214, 32%, 91%)',
    },
    colorsDark: {
      primary: 'hsl(214, 100%, 64%)',
      primaryForeground: 'hsl(0, 0%, 98%)',
      accent: 'hsl(24, 100%, 58%)',
      accentForeground: 'hsl(0, 0%, 98%)',
      background: 'hsl(224, 71%, 4%)',
      card: 'hsl(224, 71%, 6%)',
      foreground: 'hsl(210, 20%, 98%)',
      mutedForeground: 'hsl(217, 10%, 64%)',
      border: 'hsl(215, 27%, 18%)',
    },
    typescale: {
      headingFont: 'Inter',
      bodyFont: 'Inter',
      headingStack: '"Inter", system-ui, sans-serif',
      bodyStack: '"Inter", system-ui, sans-serif',
      sizeHero: '3.75rem',
      sizeH1: '2.25rem',
      sizeH2: '1.75rem',
      sizeH3: '1.375rem',
      sizeBody: '1rem',
      sizeSmall: '0.875rem',
      headingWeight: '600',
      headingTracking: '-0.01em',
      headingLeading: '1.25',
      bodyLeading: '1.6',
    },
    shape: {
      radiusSm: '0.375rem',
      radiusMd: '0.75rem',
      radiusLg: '0.75rem',
      radiusPill: '9999px',
      shadowCard: '0 2px 8px rgba(59,130,246,0.08)',
      shadowHover: '0 8px 24px rgba(59,130,246,0.15)',
    },
    motion: {
      transitionBase: '200ms ease',
      transitionHero: '400ms ease',
    },
    components: {
      btnPrimaryBg: 'hsl(214, 100%, 59%)',
      btnPrimaryFg: '#FFFFFF',
      btnPrimaryBorder: 'none',
      btnRadius: '0.75rem',
      btnTracking: 'normal',
      btnTransform: 'none',
      btnWeight: '600',
      cardBg: 'hsl(0, 0%, 100%)',
      cardBorder: '1px solid hsl(214, 32%, 91%)',
      cardRadius: '0.75rem',
      cardImageRadius: '0.5rem',
      badgeBg: 'hsl(214, 100%, 95%)',
      badgeFg: 'hsl(214, 100%, 40%)',
      badgeRadius: '9999px',
      inputBg: 'hsl(0, 0%, 100%)',
      inputBorder: '1px solid hsl(214, 32%, 91%)',
      inputFocusRing: 'hsl(214, 100%, 59%)',
      inputRadius: '0.375rem',
      heroMinHeight: '520px',
      heroHeadingColor: '#FFFFFF',
      heroSubheadingColor: 'rgba(255,255,255,0.85)',
    },
  },

  // ─── Hoosier Hills — South Central Indiana ───────────────────────────────
  // Renamed from Monroe County. Legacy key 'monroe-county' kept as alias below.

  'hoosier-hills': {
    geographySlug: 'hoosier-hills',
    displayName: 'Hoosier Hills',
    defaultDark: false,
    heroTreatment: 'warm-overlay',
    colors: {
      primary: '#F4EBDD',
      primaryForeground: '#7A0000',
      accent: '#990000',
      accentForeground: '#FFF4E1',
      background: '#F8F1E6',
      card: '#FFFDF8',
      foreground: '#241512',
      mutedForeground: '#705B52',
      border: '#DACCC0',
    },
    colorsDark: {
      primary: '#E8D8C3',
      primaryForeground: '#4B0E0E',
      accent: '#C63A3A',
      accentForeground: '#FFF4E1',
      background: '#160D0C',
      card: '#241514',
      foreground: '#FAF1E4',
      mutedForeground: '#BBAA9C',
      border: '#4A312D',
    },
    typescale: {
      headingFont: 'Space Grotesk',
      bodyFont: 'Inter',
      headingStack: '"Space Grotesk", "BentonSans", system-ui, sans-serif',
      bodyStack: '"Inter", system-ui, sans-serif',
      sizeHero: '4.25rem',
      sizeH1: '2.625rem',
      sizeH2: '1.875rem',
      sizeH3: '1.375rem',
      sizeBody: '1rem',
      sizeSmall: '0.875rem',
      headingWeight: '600',
      headingTracking: '-0.02em',
      headingLeading: '1.15',
      bodyLeading: '1.65',
    },
    shape: {
      radiusSm: '0.375rem',
      radiusMd: '0.75rem',
      radiusLg: '1.25rem',
      radiusPill: '9999px',
      shadowCard: '0 6px 18px rgba(31,26,22,0.06)',
      shadowHover: '0 12px 28px rgba(122,0,0,0.12)',
    },
    motion: {
      transitionBase: '220ms ease',
      transitionHero: '420ms ease',
    },
    components: {
      btnPrimaryBg: '#990000',
      btnPrimaryFg: '#FFF4E1',
      btnPrimaryBorder: 'none',
      btnRadius: '9999px',
      btnTracking: 'normal',
      btnTransform: 'none',
      btnWeight: '600',
      cardBg: '#FFFDF8',
      cardBorder: '1px solid #DACCC0',
      cardRadius: '1.25rem',
      cardImageRadius: '0.875rem',
      badgeBg: '#FFF1EB',
      badgeFg: '#990000',
      badgeRadius: '9999px',
      inputBg: '#FFFDF8',
      inputBorder: '1px solid #DACCC0',
      inputFocusRing: '#990000',
      inputRadius: '0.75rem',
      heroMinHeight: '520px',
      heroHeadingColor: '#FFFFFF',
      heroSubheadingColor: 'rgba(255,255,255,0.85)',
    },
    componentsDark: {
      cardBg: '#241514',
      cardBorder: '1px solid #4A312D',
      badgeBg: 'rgba(198,58,58,0.18)',
      badgeFg: '#F8E0D4',
      inputBg: '#1A1010',
      inputBorder: '1px solid #4A312D',
      inputFocusRing: '#C63A3A',
      btnPrimaryBg: '#C63A3A',
      btnPrimaryFg: '#FFF4E1',
    },
  },

  // ─── Bloomington ──────────────────────────────────────────────────────────

  bloomington: {
    geographySlug: 'bloomington',
    displayName: 'Bloomington',
    defaultDark: false,
    heroTreatment: 'warm-overlay',
    colors: {
      primary: '#990000',
      primaryForeground: '#FFF4E1',
      accent: '#F4EBDD',
      accentForeground: '#7A0000',
      background: '#FAF4EA',
      card: '#FFFDF8',
      foreground: '#241512',
      mutedForeground: '#6A554E',
      border: '#DDCFC2',
    },
    colorsDark: {
      primary: '#C63A3A',
      primaryForeground: '#FFF4E1',
      accent: '#E8D8C3',
      accentForeground: '#2B0D0D',
      background: '#170D0D',
      card: '#241514',
      foreground: '#FBF2E4',
      mutedForeground: '#C4B1A2',
      border: '#4A302C',
    },
    typescale: {
      headingFont: 'Barlow Condensed',
      bodyFont: 'Inter',
      headingStack: '"Benton Sans Condensed", "Barlow Condensed", "Arial Narrow", sans-serif',
      bodyStack: '"BentonSans", "Inter", system-ui, sans-serif',
      sizeHero: '5rem',
      sizeH1: '3rem',
      sizeH2: '2.125rem',
      sizeH3: '1.5rem',
      sizeBody: '1rem',
      sizeSmall: '0.875rem',
      headingWeight: '700',
      headingTracking: '-0.03em',
      headingLeading: '1.05',
      bodyLeading: '1.6',
    },
    shape: {
      radiusSm: '0.125rem',
      radiusMd: '0.25rem',
      radiusLg: '0.75rem',
      radiusPill: '9999px',
      shadowCard: '0 4px 14px rgba(34,23,20,0.06), 0 1px 0 rgba(153,0,0,0.08)',
      shadowHover: '0 10px 24px rgba(153,0,0,0.16)',
    },
    motion: {
      transitionBase: '180ms ease-out',
      transitionHero: '360ms ease-out',
    },
    components: {
      btnPrimaryBg: '#990000',
      btnPrimaryFg: '#FFF4E1',
      btnPrimaryBorder: 'none',
      btnRadius: '0.25rem',
      btnTracking: '0.04em',
      btnTransform: 'uppercase',
      btnWeight: '700',
      cardBg: '#FFFDF8',
      cardBorder: '1px solid #DDCFC2',
      cardRadius: '0.75rem',
      cardImageRadius: '0.5rem',
      badgeBg: '#FFF1EB',
      badgeFg: '#990000',
      badgeRadius: '0.25rem',
      inputBg: '#FFFDF8',
      inputBorder: '1px solid #D3C2B5',
      inputFocusRing: '#990000',
      inputRadius: '0.25rem',
      heroMinHeight: '540px',
      heroHeadingColor: '#FFFFFF',
      heroSubheadingColor: 'rgba(255,255,255,0.85)',
    },
    componentsDark: {
      cardBg: '#221515',
      cardBorder: '1px solid #4A302D',
      badgeBg: 'rgba(198,58,58,0.18)',
      badgeFg: '#F7D4D4',
      inputBg: '#170F0F',
      inputBorder: '1px solid #4A302D',
      inputFocusRing: '#C63A3A',
      btnPrimaryBg: '#C63A3A',
    },
  },

  // ─── Ellettsville ────────────────────────────────────────────────────────
  // Concept: Indiana limestone quarry town — burnt sienna terracotta, autumnal warmth,
  // distinct from both Monroe County's forest green and Bloomington's IU crimson.

  ellettsville: {
    geographySlug: 'ellettsville',
    displayName: 'Ellettsville',
    defaultDark: false,
    heroTreatment: 'high-contrast',
    colors: {
      primary: '#002D6C', // Indiana state flag navy blue
      primaryForeground: '#FFFFFF',
      accent: '#FFCD00', // Indiana state flag gold
      accentForeground: '#001A40',
      background: '#F4F7FB', // Cool blue-white
      card: '#FFFFFF',
      foreground: '#0A1628', // Very dark navy
      mutedForeground: '#4A5568',
      border: '#C8D5E8',
    },
    colorsDark: {
      primary: '#4D7FCC', // Lighter blue for dark mode readability
      primaryForeground: '#FFFFFF',
      accent: '#FFCD00',
      accentForeground: '#001A40',
      background: '#060D1F',
      card: '#0D1A35',
      foreground: '#E8EEF8',
      mutedForeground: '#7A8CA8',
      border: '#1A2E52',
    },
    typescale: {
      headingFont: 'Playfair Display',
      bodyFont: 'Inter',
      headingStack: '"Playfair Display", Georgia, serif',
      bodyStack: '"Inter", system-ui, sans-serif',
      sizeHero: '4.25rem',
      sizeH1: '2.75rem',
      sizeH2: '2rem',
      sizeH3: '1.5rem',
      sizeBody: '1rem',
      sizeSmall: '0.875rem',
      headingWeight: '600',
      headingTracking: '-0.015em',
      headingLeading: '1.15',
      bodyLeading: '1.68',
    },
    shape: {
      radiusSm: '0.25rem',
      radiusMd: '0.75rem',
      radiusLg: '1rem',
      radiusPill: '9999px',
      shadowCard: '0 4px 14px rgba(0,45,108,0.07), 0 1px 0 rgba(0,45,108,0.05)',
      shadowHover: '0 10px 24px rgba(0,45,108,0.16)',
    },
    motion: {
      transitionBase: '220ms ease',
      transitionHero: '420ms ease',
    },
    components: {
      btnPrimaryBg: '#002D6C',
      btnPrimaryFg: '#FFFFFF',
      btnPrimaryBorder: 'none',
      btnRadius: '9999px',
      btnTracking: 'normal',
      btnTransform: 'none',
      btnWeight: '600',
      cardBg: '#FFFFFF',
      cardBorder: '1px solid #C8D5E8',
      cardRadius: '1rem',
      cardImageRadius: '0.75rem',
      badgeBg: '#002D6C',
      badgeFg: '#FFCD00',
      badgeRadius: '9999px',
      inputBg: '#FFFFFF',
      inputBorder: '1px solid #C8D5E8',
      inputFocusRing: '#002D6C',
      inputRadius: '0.75rem',
      heroMinHeight: '520px',
      heroHeadingColor: '#FFFFFF',
      heroSubheadingColor: 'rgba(255,255,255,0.84)',
    },
    componentsDark: {
      cardBg: '#0D1A35',
      cardBorder: '1px solid #1A2E52',
      badgeBg: 'rgba(77,127,204,0.2)',
      badgeFg: '#FFCD00',
      inputBg: '#060D1F',
      inputBorder: '1px solid #1A2E52',
      inputFocusRing: '#4D7FCC',
      btnPrimaryBg: '#4D7FCC',
      btnPrimaryFg: '#FFFFFF',
    },
  },

  // ─── Lafayette ────────────────────────────────────────────────────────────

  lafayette: {
    geographySlug: 'lafayette',
    displayName: 'Lafayette',
    defaultDark: false,
    heroTreatment: 'high-contrast',
    colors: {
      primary: '#1A1A1A',
      primaryForeground: '#CFB991',
      accent: '#CFB991',
      accentForeground: '#1A1A1A',
      background: '#F5F0E8',
      card: '#FDFAF4',
      foreground: '#1A1A1A',
      mutedForeground: '#5C5040',
      border: '#DDD3C0',
    },
    colorsDark: {
      primary: '#CFB991',
      primaryForeground: '#1A1A1A',
      accent: '#E8D4A8',
      accentForeground: '#1A1A1A',
      background: '#111110',
      card: '#1E1E1A',
      foreground: '#F5F0E8',
      mutedForeground: '#9B8C6E',
      border: '#3A3428',
    },
    typescale: {
      headingFont: 'Space Grotesk',
      bodyFont: 'Inter',
      headingStack: '"Space Grotesk", system-ui, sans-serif',
      bodyStack: '"Inter", system-ui, sans-serif',
      sizeHero: '4.5rem',
      sizeH1: '2.75rem',
      sizeH2: '2rem',
      sizeH3: '1.5rem',
      sizeBody: '1rem',
      sizeSmall: '0.875rem',
      headingWeight: '700',
      headingTracking: '-0.02em',
      headingLeading: '1.15',
      bodyLeading: '1.6',
    },
    shape: {
      radiusSm: '0.125rem',
      radiusMd: '0.25rem',
      radiusLg: '0.5rem',
      radiusPill: '9999px',
      shadowCard: '0 1px 4px rgba(0,0,0,0.12)',
      shadowHover: '0 4px 0px #CFB991, 0 6px 12px rgba(0,0,0,0.15)',
    },
    motion: {
      transitionBase: '200ms ease-in-out',
      transitionHero: '400ms ease-in-out',
    },
    components: {
      btnPrimaryBg: '#1A1A1A',
      btnPrimaryFg: '#CFB991',
      btnPrimaryBorder: '1px solid #CFB991',
      btnRadius: '0.25rem',
      btnTracking: '-0.02em',
      btnTransform: 'none',
      btnWeight: '700',
      cardBg: '#FDFAF4',
      cardBorder: '1px solid #DDD3C0',
      cardRadius: '0.5rem',
      cardImageRadius: '0.25rem',
      badgeBg: '#1A1A1A',
      badgeFg: '#CFB991',
      badgeRadius: '0.125rem',
      inputBg: '#FDFAF4',
      inputBorder: '1px solid #1A1A1A',
      inputFocusRing: '#CFB991',
      inputRadius: '0.25rem',
      heroMinHeight: '480px',
      heroHeadingColor: '#CFB991',
      heroSubheadingColor: 'rgba(207,185,145,0.75)',
    },
  },

  // ─── Indianapolis ─────────────────────────────────────────────────────────

  indianapolis: {
    geographySlug: 'indianapolis',
    displayName: 'Indianapolis',
    defaultDark: false,
    heroTreatment: 'high-contrast',
    colors: {
      primary: '#003087',
      primaryForeground: '#FFFFFF',
      accent: '#FDBB30',
      accentForeground: '#003087',
      background: '#F0F4FA',
      card: '#FFFFFF',
      foreground: '#0A1628',
      mutedForeground: '#4A5568',
      border: '#CBD5E8',
    },
    colorsDark: {
      primary: '#4D7FCC',
      primaryForeground: '#FFFFFF',
      accent: '#FDBB30',
      accentForeground: '#060D1F',
      background: '#060D1F',
      card: '#0D1A35',
      foreground: '#E8EEF8',
      mutedForeground: '#7A8CA8',
      border: '#1A2E52',
    },
    typescale: {
      headingFont: 'Barlow Condensed',
      bodyFont: 'Inter',
      headingStack: '"Barlow Condensed", system-ui, sans-serif',
      bodyStack: '"Inter", system-ui, sans-serif',
      sizeHero: '5.5rem',
      sizeH1: '3.25rem',
      sizeH2: '2.5rem',
      sizeH3: '1.75rem',
      sizeBody: '1rem',
      sizeSmall: '0.875rem',
      headingWeight: '700',
      headingTracking: '-0.03em',
      headingLeading: '1.05',
      bodyLeading: '1.55',
    },
    shape: {
      radiusSm: '0.25rem',
      radiusMd: '0.375rem',
      radiusLg: '0.5rem',
      radiusPill: '9999px',
      shadowCard: '0 1px 4px rgba(0,48,135,0.08)',
      shadowHover: '0 6px 0px #FDBB30, 0 8px 16px rgba(0,48,135,0.15)',
    },
    motion: {
      transitionBase: '150ms ease-in',
      transitionHero: '300ms ease-in',
    },
    components: {
      btnPrimaryBg: '#003087',
      btnPrimaryFg: '#FFFFFF',
      btnPrimaryBorder: 'none',
      btnRadius: '0.375rem',
      btnTracking: '-0.03em',
      btnTransform: 'uppercase',
      btnWeight: '700',
      cardBg: '#FFFFFF',
      cardBorder: '1px solid #CBD5E8',
      cardRadius: '0.5rem',
      cardImageRadius: '0.25rem',
      badgeBg: '#003087',
      badgeFg: '#FDBB30',
      badgeRadius: '0.25rem',
      inputBg: '#FFFFFF',
      inputBorder: '1px solid #CBD5E8',
      inputFocusRing: '#003087',
      inputRadius: '0.375rem',
      heroMinHeight: '560px',
      heroHeadingColor: '#FFFFFF',
      heroSubheadingColor: 'rgba(255,255,255,0.85)',
    },
  },

  // ─── Online ───────────────────────────────────────────────────────────────

  online: {
    geographySlug: 'online',
    displayName: 'Online',
    defaultDark: true,
    heroTreatment: 'dark-glass',
    colors: {
      // Light mode palette — sky/cloud identity, slightly deeper primaries for WCAG AA on white
      primary: '#0284C7',
      primaryForeground: '#FFFFFF',
      accent: '#7C3AED',
      accentForeground: '#FFFFFF',
      background: '#F0F9FF',
      card: '#FFFFFF',
      foreground: '#0C1A2E',
      mutedForeground: '#475569',
      border: '#BAE6FD',
    },
    colorsDark: {
      primary: '#0EA5E9',
      primaryForeground: '#FFFFFF',
      accent: '#8B5CF6',
      accentForeground: '#FFFFFF',
      background: '#0F0F13',
      card: '#1A1A24',
      foreground: '#F1F5F9',
      mutedForeground: '#94A3B8',
      border: '#2A2A3A',
    },
    typescale: {
      headingFont: 'Plus Jakarta Sans',
      bodyFont: 'Inter',
      headingStack: '"Plus Jakarta Sans", system-ui, sans-serif',
      bodyStack: '"Inter", system-ui, sans-serif',
      sizeHero: '4rem',
      sizeH1: '2.5rem',
      sizeH2: '1.875rem',
      sizeH3: '1.375rem',
      sizeBody: '1rem',
      sizeSmall: '0.875rem',
      headingWeight: '600',
      headingTracking: '-0.015em',
      headingLeading: '1.2',
      bodyLeading: '1.65',
    },
    shape: {
      radiusSm: '0.5rem',
      radiusMd: '0.75rem',
      radiusLg: '1rem',
      radiusPill: '9999px',
      shadowCard: '0 0 0 1px rgba(139,92,246,0.15), 0 4px 16px rgba(14,165,233,0.08)',
      shadowHover: '0 0 0 1px rgba(139,92,246,0.4), 0 8px 24px rgba(14,165,233,0.18)',
    },
    motion: {
      transitionBase: '250ms cubic-bezier(0.16,1,0.3,1)',
      transitionHero: '500ms cubic-bezier(0.16,1,0.3,1)',
    },
    components: {
      btnPrimaryBg: '#0EA5E9',
      btnPrimaryFg: '#FFFFFF',
      btnPrimaryBorder: 'none',
      btnRadius: '0.75rem',
      btnTracking: 'normal',
      btnTransform: 'none',
      btnWeight: '600',
      cardBg: '#1A1A24',
      cardBorder: '1px solid #2A2A3A',
      cardRadius: '1rem',
      cardImageRadius: '0.75rem',
      badgeBg: 'rgba(139,92,246,0.2)',
      badgeFg: '#8B5CF6',
      badgeRadius: '9999px',
      inputBg: '#1A1A24',
      inputBorder: '1px solid #2A2A3A',
      inputFocusRing: '#0EA5E9',
      inputRadius: '0.5rem',
      heroMinHeight: '600px',
      heroHeadingColor: '#FFFFFF',
      heroSubheadingColor: 'rgba(241,245,249,0.8)',
    },
    componentsLight: {
      cardBg: '#FFFFFF',
      cardBorder: '1px solid #BAE6FD',
      badgeBg: 'rgba(2,132,199,0.1)',
      badgeFg: '#0284C7',
      inputBg: '#FFFFFF',
      inputBorder: '1px solid #BAE6FD',
      inputFocusRing: '#0284C7',
      btnPrimaryBg: '#0284C7',
      btnPrimaryFg: '#FFFFFF',
    },
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const DEFAULT_GEOGRAPHY_SLUG = 'bloomington';

export function getGeographyTheme(slug: string): CityTheme {
  return CITY_THEMES[slug] ?? CITY_THEMES[DEFAULT_GEOGRAPHY_SLUG];
}

/** @deprecated Use getGeographyTheme */
export const DEFAULT_CITY_ID = DEFAULT_GEOGRAPHY_SLUG;
/** @deprecated Use getGeographyTheme */
export const getCityTheme = getGeographyTheme;

/** All unique Google Font families needed across all geography themes */
export function getRequiredFonts(): string[] {
  const fonts = new Set<string>();
  for (const theme of Object.values(CITY_THEMES)) {
    fonts.add(theme.typescale.headingFont);
    fonts.add(theme.typescale.bodyFont);
  }
  fonts.add('Inter');
  return Array.from(fonts).filter(f => f !== 'Inter');
}
